<template>
  <div
    v-if="current"
    :key="current.entryLogId"
    class="member-entry-popup"
    :class="{ 'member-entry-popup--professional': isProfessional }"
    :data-testid="$testIds.memberEntryPopup.root"
    :data-member-entry-log-id="current.entryLogId"
    role="status"
    aria-live="polite"
    :aria-label="$t('memberEntryPopup.title')"
    @mouseenter="pauseTimer"
    @mouseleave="resumeTimer"
  >
    <div class="member-entry-popup__banner">
      <span class="member-entry-popup__banner-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
             stroke="currentColor" stroke-width="2.5"
             stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </span>
      <span class="member-entry-popup__banner-text">
        {{ isProfessional ? $t('memberEntryPopup.professionalTitle') : $t('memberEntryPopup.title') }}
      </span>
      <button
        type="button"
        class="member-entry-popup__close"
        :data-testid="$testIds.memberEntryPopup.dismiss"
        :aria-label="$t('memberEntryPopup.dismiss')"
        @click="dismiss"
      >×</button>
    </div>

    <div class="member-entry-popup__content">
      <div class="member-entry-popup__avatar">
        <ProfileImageState :src="avatarSrc" :alt="displayName" :name="displayName" />
      </div>

      <div class="member-entry-popup__body">
        <div class="member-entry-popup__name">{{ displayName }}</div>

        <div v-if="isProfessional" class="member-entry-popup__role-line">
          <span class="member-entry-popup__role-badge">{{ roleLabel }}</span>
          <span v-if="current.isAlsoMember" class="member-entry-popup__also-member">
            {{ $t('memberEntryPopup.alsoMember') }}
          </span>
        </div>

        <div v-else-if="current.membershipType" class="member-entry-popup__membership-type">
          {{ current.membershipType }}
        </div>

        <div v-if="!isProfessional" class="member-entry-popup__meta">
          <span v-if="current.membershipEndDate" class="member-entry-popup__meta-item">
            <span class="member-entry-popup__meta-label">
              {{ $t('memberEntryPopup.endsOn', { date: formatDate(current.membershipEndDate) }) }}
            </span>
          </span>
          <span
            v-if="current.remainingEntries !== null && current.remainingEntries !== undefined"
            class="member-entry-popup__meta-item member-entry-popup__meta-item--entries"
          >
            <span
              v-if="entriesMembershipName"
              class="member-entry-popup__entries-source"
            >{{ entriesMembershipName }}</span>
            <strong>{{ current.remainingEntries }}</strong>
            <span class="member-entry-popup__meta-label">
              {{ $tc('memberEntryPopup.entriesLeft', current.remainingEntries) }}
            </span>
          </span>
          <span
            v-if="hasOpenBalance"
            class="member-entry-popup__meta-item member-entry-popup__meta-item--balance"
            :class="{ 'is-negative': current.openBalance < 0 }"
          >
            <span class="member-entry-popup__meta-label">{{ $t('memberEntryPopup.openBalance') }}:</span>
            <strong>{{ formatBalance(current.openBalance) }}</strong>
          </span>
        </div>

        <div class="member-entry-popup__gate">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round"
               aria-hidden="true">
            <rect x="4" y="2" width="16" height="20" rx="1" />
            <path d="M9 22V12h6v10" />
          </svg>
          {{ current.gateName || $t('memberEntryPopup.gate') }}
          · {{ formatTime(current.entryTimestamp) }}
        </div>
      </div>
    </div>

    <div v-if="backlogCount > 0" class="member-entry-popup__backlog" aria-hidden="true">
      {{ $t('memberEntryPopup.moreCount', { count: backlogCount }) }}
    </div>

    <div class="member-entry-popup__progress">
      <div
        class="member-entry-popup__progress-bar"
        :style="{ width: `${progressPct}%` }"
      ></div>
    </div>
  </div>
</template>

<script>
import { parseAvatarDocumentId } from '@/utils/avatarDocumentUrl';
import ProfileImageState from '@/components/ProfileImageState.vue';

const AUTO_DISMISS_MS = 6000;

export default {
  name: 'MemberEntryPopup',
  components: { ProfileImageState },
  data() {
    return {
      timerId: null,
      timerStartedAt: 0,
      remainingMs: AUTO_DISMISS_MS,
      timerPaused: false,
      avatarSrc: null,
    };
  },
  computed: {
    queue() {
      return this.$store.state.popups.memberEntryQueue;
    },
    current() {
      return this.queue.length > 0 ? this.queue[0] : null;
    },
    backlogCount() {
      return Math.max(0, this.queue.length - 1);
    },
    avatarDocumentId() {
      // Prefer the id parsed out of avatarDocumentURL — the numeric field loses
      // precision past Number.MAX_SAFE_INTEGER. Fall back to the numeric field
      // only when no URL is supplied.
      const fromUrl = parseAvatarDocumentId(this.current && this.current.avatarDocumentURL);
      if (fromUrl) return fromUrl;
      const numericId = this.current && this.current.avatarDocumentId;
      return numericId ? String(numericId) : null;
    },
    avatarCacheEntry() {
      const id = this.avatarDocumentId;
      if (!id) return undefined;
      return this.$store.state.settings.images[id];
    },
    // The count can belong to a membership other than the one on the line above: boxing entries
    // sold beside a gym plan, which a gym walk deliberately leaves alone. Reception still needs the
    // number, so the membership it counts down is named beside it -- unnamed, it read as the gym
    // plan's own count and made the walk look like it had spent a boxing entry.
    entriesMembershipName() {
      return (this.current && this.current.remainingEntriesMembershipType) || '';
    },
    hasOpenBalance() {
      const balance = this.current && this.current.openBalance;
      return balance !== null && balance !== undefined && Number(balance) !== 0;
    },
    role() {
      return (this.current && this.current.role) || 'MEMBER';
    },
    isProfessional() {
      return this.role !== 'MEMBER';
    },
    roleLabel() {
      const key = `memberEntryPopup.roles.${this.role}`;
      const label = this.$t(key);
      return label === key ? this.role : label;
    },
    displayName() {
      return (this.current && this.current.memberName)
        || this.$t('memberEntryPopup.member');
    },
    progressPct() {
      return Math.max(0, Math.min(100, (this.remainingMs / AUTO_DISMISS_MS) * 100));
    },
  },
  watch: {
    'current.entryLogId': {
      immediate: true,
      handler(newId) {
        this.loadAvatar();
        if (newId) {
          this.startTimer();
        } else {
          this.stopTimer();
        }
      },
    },
    avatarCacheEntry(entry) {
      if (entry) this.avatarSrc = entry.data || null;
    },
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    dismiss() {
      this.stopTimer();
      this.$store.commit('dismissCurrentMemberEntry');
    },
    loadAvatar() {
      const id = this.avatarDocumentId;
      this.avatarSrc = id ? (this.fetchCachedImage(id, () => null) || null) : null;
    },
    startTimer() {
      this.stopTimer();
      this.remainingMs = AUTO_DISMISS_MS;
      this.timerPaused = false;
      this.timerStartedAt = Date.now();
      this.timerId = window.setTimeout(this.dismiss, AUTO_DISMISS_MS);
    },
    stopTimer() {
      if (this.timerId !== null) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
    },
    pauseTimer() {
      if (this.timerId === null || this.timerPaused) {
        return;
      }
      const elapsed = Date.now() - this.timerStartedAt;
      this.remainingMs = Math.max(0, this.remainingMs - elapsed);
      window.clearTimeout(this.timerId);
      this.timerId = null;
      this.timerPaused = true;
    },
    resumeTimer() {
      if (!this.timerPaused) {
        return;
      }
      this.timerPaused = false;
      this.timerStartedAt = Date.now();
      this.timerId = window.setTimeout(this.dismiss, this.remainingMs);
    },
    formatDate(value) {
      if (!value) return '';
      try {
        return new Date(value).toLocaleDateString();
      } catch (e) { return value; }
    },
    formatTime(value) {
      if (!value) return '';
      try {
        return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) { return value; }
    },
    formatBalance(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '';
      try {
        return new Intl.NumberFormat(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(n);
      } catch (e) { return n.toFixed(2); }
    },
  },
};
</script>

<style scoped>
.member-entry-popup {
  position: fixed;
  top: 88px;
  right: 24px;
  z-index: 1080;
  width: 380px;
  background: var(--fitnet-popup-bg);
  border-radius: 14px;
  box-shadow: var(--fitnet-popup-shadow);
  overflow: hidden;
  animation: member-entry-popup-in 320ms cubic-bezier(0.16, 1, 0.3, 1),
             member-entry-popup-pulse 1400ms ease-out 320ms 2;
}

@keyframes member-entry-popup-in {
  0%   { transform: translateX(40px) scale(0.96); opacity: 0; }
  100% { transform: translateX(0) scale(1);       opacity: 1; }
}

@keyframes member-entry-popup-pulse {
  0%   { box-shadow: var(--fitnet-popup-shadow), 0 0 0 0 var(--fitnet-popup-pulse-color); }
  100% { box-shadow: var(--fitnet-popup-shadow), 0 0 0 14px transparent; }
}

.member-entry-popup__banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--fitnet-popup-banner-bg);
  color: var(--fitnet-popup-bg);
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.2px;
}

.member-entry-popup__banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  color: var(--fitnet-popup-bg);
}

.member-entry-popup__banner-text {
  flex: 1 1 auto;
  text-transform: uppercase;
}

.member-entry-popup__close {
  background: transparent;
  border: 0;
  color: var(--fitnet-popup-bg);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0.85;
}

.member-entry-popup__close:hover {
  opacity: 1;
}

.member-entry-popup__content {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 16px 18px;
}

.member-entry-popup__avatar {
  flex: 0 0 64px;
  font-size: 64px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--fitnet-popup-avatar-bg);
  border: 3px solid var(--fitnet-popup-bg);
  box-shadow: var(--fitnet-popup-avatar-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
}

.member-entry-popup__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-entry-popup__body {
  flex: 1 1 auto;
  min-width: 0;
}

.member-entry-popup__name {
  font-weight: 700;
  font-size: 17px;
  color: var(--fitnet-popup-text);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-entry-popup__membership-type {
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  background: var(--fitnet-popup-accent-soft-bg);
  color: var(--fitnet-popup-accent-dark);
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.member-entry-popup__role-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.member-entry-popup__role-badge {
  display: inline-block;
  padding: 3px 10px;
  background: var(--fitnet-popup-professional-badge-bg, #6d28d9);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.member-entry-popup__also-member {
  font-size: 11px;
  font-weight: 600;
  color: var(--fitnet-popup-text-muted);
  text-transform: none;
}

.member-entry-popup--professional .member-entry-popup__banner {
  background: var(--fitnet-popup-professional-banner-bg, #6d28d9);
}

.member-entry-popup__meta {
  margin-top: 6px;
  font-size: 12px;
  color: var(--fitnet-popup-text-muted);
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
}

.member-entry-popup__entries-source {
  padding: 1px 6px;
  background: var(--fitnet-popup-accent-soft-bg);
  color: var(--fitnet-popup-accent-dark);
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  margin-inline-end: 4px;
}

.member-entry-popup__meta-item--entries strong {
  color: var(--fitnet-popup-accent-dark);
  font-size: 12px;
  font-weight: 700;
  margin-right: 3px;
}

.member-entry-popup__meta-item--balance strong {
  color: var(--fitnet-popup-accent-dark);
  font-size: 12px;
  font-weight: 700;
  margin-left: 3px;
}

.member-entry-popup__meta-item--balance.is-negative strong,
.member-entry-popup__meta-item--balance.is-negative .member-entry-popup__meta-label {
  color: var(--fitnet-danger-badge-color);
}

.member-entry-popup__gate {
  margin-top: 6px;
  font-size: 11px;
  color: var(--fitnet-popup-text-subtle);
  display: flex;
  align-items: center;
  gap: 5px;
}

.member-entry-popup__backlog {
  position: absolute;
  top: 8px;
  right: 44px;
  font-size: 10px;
  font-weight: 700;
  color: var(--fitnet-popup-accent-dark);
  background: var(--fitnet-popup-bg);
  border-radius: 999px;
  padding: 2px 8px;
  letter-spacing: 0.3px;
}

.member-entry-popup__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--fitnet-popup-track-bg);
}

.member-entry-popup__progress-bar {
  height: 100%;
  background: var(--fitnet-popup-progress-bg);
  transition: width 100ms linear;
}
</style>
